import { useEffect, useState } from 'react';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/States';
import { useSession } from '@/stores/session';
import { useFormFactor } from '@/lib/formFactor';
import { qty } from '@/lib/format';
import { setProductActive, type ProductStatusPreview } from './api';
import { useResource } from './kit/hooks';
import s from './ProductStatusDialog.module.css';

const READONLY = import.meta.env.VITE_READONLY !== '0';

export function ProductStatusDialog({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const tablet = useFormFactor() === 'tablet';
  const can = useSession((state) => state.can);
  const { data, loading, error, reload } = useResource<ProductStatusPreview>(`/api/v2/products/${encodeURIComponent(id)}/status`);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [uncertain, setUncertain] = useState(false);
  useEffect(() => {
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [saving, onClose]);

  const p = data?.product;
  const refs = data ? Number(data.bundleCount > 0) + Number(data.orderCount > 0) + Number(data.purchaseCount > 0) + Number(data.stocks.length > 0) : 0;
  const title = p?.isActive === false ? `重新启用产品「${p.name}」？` : refs ? `删除产品「${p?.name || '…'}」？` : `停用产品「${p?.name || '…'}」？`;
  const submit = async () => {
    if (!p || READONLY || !can('products.write') || saving || uncertain) return;
    setSaving(true); setMessage('');
    try {
      await setProductActive(id, !p.isActive, p.updatedAt);
      onChanged();
      window.dispatchEvent(new Event('ims:v2:refresh'));
      onClose();
    } catch (cause) {
      const unknown = !(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500;
      setUncertain(unknown);
      setMessage(unknown ? '操作结果未确认，请刷新产品状态核对，不要重复提交。' : cause instanceof Error ? cause.message : '操作失败');
    } finally { setSaving(false); }
  };

  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <section className={`${s.dialog} ${tablet && refs > 0 ? s.tabletDialog : ''} ${tablet && refs >= 4 ? s.tabletDialogFull : ''}`} role="dialog" aria-modal="true" aria-labelledby={tablet && refs > 0 ? 'product-status-tablet-title' : 'product-status-title'}>
      <header className={s.head}><h2 id="product-status-title">{title}</h2><button type="button" aria-label="关闭" onClick={onClose} disabled={saving}>×</button></header>
      <div className={s.body}>
        {loading && !data ? <Skeleton lines={5} /> : error ? <div className={s.error} role="alert">引用信息加载失败：{error.message}<button type="button" onClick={reload}>重试</button></div> : data && <>
          {tablet && p?.isActive && refs > 0 ? <>
            <div className={s.tabletIntro}><span className={s.tabletAlert}><Icon name="alert" size={20} /></span><h2 id="product-status-tablet-title">{title}</h2><p>它被 {refs} 处引用，不能删除。删掉会让这些记录找不到产品：</p></div>
            <ul className={`${s.refs} ${s.tabletRefs}`}>
              {data.bundleCount > 0 && <li><strong>套装</strong><div>{data.bundles.length ? data.bundles.map((item) => <span key={item.id}>{item.name}</span>) : <span>{data.bundleCount} 个套装</span>}<small>组件之一</small></div></li>}
              {data.orderCount > 0 && <li><strong>订单</strong><div><span>{data.orderCount} 张未完成</span><small>{data.orders.map((item) => item.orderNo).filter(Boolean).join('、')}</small></div></li>}
              {data.purchaseCount > 0 && <li><strong>采购</strong><div><span>{data.purchases.map((item) => item.purchaseNo).filter(Boolean).join('、') || `${data.purchaseCount} 张未完成`}</span><small>{data.purchaseCount} 张未完成采购</small></div></li>}
              {data.stocks.map((stock) => <li key={stock.warehouse.id}><strong>库存</strong><div><span>{stock.warehouse.name} {qty(stock.currentQuantity)} 个</span></div></li>)}
            </ul>
            <p className={s.tabletExplain}>可以改为 <b>停用</b>：不能再新下单，历史订单、流水和库存都保留，以后可以重新启用。</p>
          </> : p?.isActive ? <>
            <p className={s.warning}><Icon name="alert" size={20} /><b>{refs ? '这个产品正在被引用，不能删除：' : '此处仅提供停用产品。'}</b></p>
            <ul className={s.refs}>
              {data.bundleCount > 0 && <li>{data.bundles.length ? `套装「${data.bundles.map((item) => item.name).join('」「')}${data.bundleCount > data.bundles.length ? '」等' : '」'}的组件` : `${data.bundleCount} 个套装的组件`}</li>}
              {data.orderCount > 0 && <li>{data.orderCount} 张未完成订单的明细</li>}
              {data.purchaseCount > 0 && <li>{data.purchaseCount} 张未完成采购的明细</li>}
              {data.stocks.length > 0 && <li>当前库存 {qty(data.stocks.reduce<number>((total, stock) => total + Number(stock.currentQuantity), 0))} 个</li>}
            </ul>
            <p className={s.explain}>可以<b>停用</b>它 —— 停用后不能新下单，但历史记录和库存都保留。</p>
          </> : <p className={s.explain}>重新启用后产品会回到 V1 和 V2 共用的产品列表。停用期间的历史数据不变。</p>}
        </>}
        {message && <p className={s.error} role="alert">{message}{uncertain && <button type="button" onClick={() => { setUncertain(false); setMessage(''); reload(); onChanged(); }}>刷新核对</button>}</p>}
        {READONLY && <p className={s.readonly}>当前为只读模式，状态修改暂不可提交。</p>}
      </div>
      <footer className={`${s.foot} ${tablet && refs > 0 ? s.tabletFoot : ''}`}><button type="button" onClick={onClose} disabled={saving}>{tablet && refs > 0 ? '暂不处理' : '取消'}</button><button type="button" className={s.primary} disabled={!p || saving || uncertain || READONLY || !can('products.write')} onClick={() => void submit()}>{saving ? '提交中…' : p?.isActive === false ? '重新启用' : '停用产品'}</button>{p?.isActive && refs > 0 && <button type="button" disabled className={s.disabled}>删除（被引用）</button>}</footer>
    </section>
  </div>;
}
